import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { DsIsland } from '@/design-system/island'
import { Button, Checkbox, Dialog, Notice } from '@/design-system/primitives'
import '@/design-system/content.css'

interface NetworkTroubleshootingModalProps {
  isOpen: boolean
  onClose: () => void
  onRetry: () => void
}

interface TroubleshootingStep {
  id: string
  title: string
  description: string
  details?: string
}

const troubleshootingSteps: TroubleshootingStep[] = [
  {
    id: 'incognito',
    title: 'Try Incognito/Private Mode',
    description: 'Open SuperSchema in an incognito window',
    details: 'This disables extensions and is the fastest way to identify if one is blocking our app.'
  },
  {
    id: 'extensions',
    title: 'Check Browser Extensions',
    description: 'Temporarily disable ad blockers & privacy tools',
    details: 'Extensions like uBlock Origin, Privacy Badger, or Brave Shields sometimes block legitimate API requests. Try disabling them for superschema.ai.'
  },
  {
    id: 'browser',
    title: 'Try a Different Browser',
    description: 'Switch from Chrome to Firefox (or vice versa)',
    details: 'If it works in another browser, the issue is browser-specific settings or extensions.'
  },
  {
    id: 'network',
    title: 'Try a Different Network',
    description: 'Switch to mobile data or a different WiFi',
    details: 'Corporate firewalls, VPNs, or network security tools can sometimes block our requests.'
  }
]

export default function NetworkTroubleshootingModal({
  isOpen,
  onClose,
  onRetry
}: NetworkTroubleshootingModalProps) {
  const [expandedStep, setExpandedStep] = useState<string | null>(null)
  const [checkedSteps, setCheckedSteps] = useState<Set<string>>(new Set())

  if (!isOpen) return null

  const toggleStep = (stepId: string) => {
    setExpandedStep(expandedStep === stepId ? null : stepId)
  }

  const toggleChecked = (stepId: string) => {
    const newChecked = new Set(checkedSteps)
    if (newChecked.has(stepId)) {
      newChecked.delete(stepId)
    } else {
      newChecked.add(stepId)
    }
    setCheckedSteps(newChecked)
  }

  return (
    <DsIsland panel={false} ground={false}>
      <Dialog
        open
        size="lg"
        onClose={onClose}
        title="We cannot reach our servers from your browser"
        lead="Our servers are fine. Something between this browser and us is blocking the request before it leaves your computer, usually an extension, a firewall, or a network rule."
        actions={
          <>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button
              onClick={() => {
                onClose()
                onRetry()
              }}
            >
              <RefreshCw aria-hidden="true" />
              I Fixed It, Try Again
            </Button>
          </>
        }
      >
        <p style={{ margin: '0 0 12px', fontWeight: 600 }}>
          Try these fixes, most likely first
        </p>

        {/*
          Two things used to share one row: clicking anywhere expanded the
          details, and clicking the number in the corner ticked it off. They
          are separate controls now, because they do separate things: a real
          checkbox for "I have tried this", and the title as the disclosure.

          The four icons are gone. A browser, a shield, a globe and a phone
          beside four instructions are decoration, and what tells the steps
          apart is what they say.
        */}
        <ul className="ds-fixlist">
          {troubleshootingSteps.map((step) => {
            const isExpanded = expandedStep === step.id
            const isChecked = checkedSteps.has(step.id)

            return (
              <li key={step.id} className="ds-fixlist__item" data-done={isChecked || undefined}>
                <Checkbox
                  label={<span className="ds-sr">I have tried: {step.title}</span>}
                  checked={isChecked}
                  onChange={() => toggleChecked(step.id)}
                />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <button
                    type="button"
                    className="ds-fixlist__title"
                    onClick={() => toggleStep(step.id)}
                    aria-expanded={step.details ? isExpanded : undefined}
                  >
                    {step.title}
                  </button>
                  <p className="ds-small ds-muted" style={{ margin: '2px 0 0' }}>
                    {step.description}
                  </p>
                  {isExpanded && step.details && (
                    <p className="ds-small ds-muted ds-fixlist__more">{step.details}</p>
                  )}
                </div>
              </li>
            )
          })}
        </ul>

        <div style={{ marginTop: 16 }}>
          <Notice>
            <span>
              <b>Still stuck?</b> Tell us through the{' '}
              <a href="/contact" className="ds-link">contact form</a> and we will help you sort it
              out. Mention &ldquo;Network Error&rdquo; in your message.
            </span>
          </Notice>
        </div>
      </Dialog>
    </DsIsland>
  )
}

/**
 * Detect if an error is a client-side network connectivity issue
 * These are errors where the request never reached the server
 */
export function isClientSideNetworkError(error: any): boolean {
  // Axios Network Error - request didn't complete
  if (error?.message === 'Network Error') {
    return true
  }

  // No response received (timeout, connection refused, etc.)
  if (error?.code === 'ERR_NETWORK' || error?.code === 'ECONNABORTED') {
    return true
  }

  // Response exists but status is undefined (shouldn't happen normally)
  if (error?.response === undefined && error?.request) {
    return true
  }

  // Fetch API errors
  if (error?.message?.includes('Failed to fetch') ||
      error?.message?.includes('NetworkError') ||
      error?.message?.includes('net::ERR_')) {
    return true
  }

  return false
}
